import { useState, useEffect, useCallback } from "react";
import {
  RolePermissionsByEntity,
  PermissionState,
  PermissionActions,
  PermissionValidationResult,
} from "../types/permissions";
import { PERMISSION_ENTITIES } from "../config/permissionsConfig";

interface UsePermissionStateProps {
  initialPermissions: RolePermissionsByEntity;
  onPermissionsChange?: (
    permissions: RolePermissionsByEntity,
    hasChanges: boolean
  ) => void;
}

interface UsePermissionStateResult extends PermissionState, PermissionActions {
  permissions: RolePermissionsByEntity;
  validationResult: PermissionValidationResult;
  getSelectedPermissionsCount: () => number;
  getTotalPermissionsCount: () => number;
}

const usePermissionState = ({
  initialPermissions,
  onPermissionsChange,
}: UsePermissionStateProps): UsePermissionStateResult => {
  const [permissions, setPermissions] =
    useState<RolePermissionsByEntity>(initialPermissions);
  const [originalPermissions, setOriginalPermissions] = useState<Set<string>>(
    new Set()
  );
  const [selectedPermissions, setSelectedPermissions] = useState<Set<string>>(
    new Set()
  );

  // Calculate if there are changes
  const hasChanges = useCallback(() => {
    if (selectedPermissions.size !== originalPermissions.size) return true;

    for (const permission of Array.from(selectedPermissions)) {
      if (!originalPermissions.has(permission)) return true;
    }

    return false;
  }, [selectedPermissions, originalPermissions]);

  // Initialize permission sets
  useEffect(() => {
    const selected = new Set<string>();
    const original = new Set<string>();

    Object.entries(initialPermissions).forEach(([entity, actions]) => {
      Object.entries(actions).forEach(([action, isSelected]) => {
        const permissionKey = `${entity}:${action}`;
        if (isSelected) {
          selected.add(permissionKey);
          original.add(permissionKey);
        }
      });
    });

    setSelectedPermissions(selected);
    setOriginalPermissions(original);
    setPermissions(initialPermissions);
  }, [initialPermissions]);

  // Handle permission changes callback
  useEffect(() => {
    if (onPermissionsChange && Object.keys(permissions).length > 0) {
      const currentHasChanges = hasChanges();
      onPermissionsChange(permissions, currentHasChanges);
    }
  }, [permissions, onPermissionsChange, hasChanges]);

  // Toggle individual permission
  const togglePermission = useCallback(
    (entity: string, action: string) => {
      const permissionKey = `${entity}:${action}`;

      setSelectedPermissions((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(permissionKey)) {
          newSet.delete(permissionKey);
        } else {
          newSet.add(permissionKey);
        }
        return newSet;
      });

      setPermissions((prev) => {
        const newPermissions = { ...prev };
        if (!newPermissions[entity]) {
          newPermissions[entity] = {};
        }
        newPermissions[entity] = {
          ...newPermissions[entity],
          [action]: !prev[entity]?.[action],
        };

        return newPermissions;
      });
    },
    [onPermissionsChange]
  );

  // Toggle all permissions for an entity
  const toggleAllEntityPermissions = useCallback(
    (entity: string, enabled: boolean) => {
      const entityConfig = PERMISSION_ENTITIES.find((e) => e.key === entity);
      if (!entityConfig) return;

      setPermissions((prev) => {
        const newPermissions = { ...prev };
        if (!newPermissions[entity]) {
          newPermissions[entity] = {};
        }

        entityConfig.actions.forEach((action) => {
          newPermissions[entity][action.key] = enabled;

          const permissionKey = `${entity}:${action.key}`;
          setSelectedPermissions((prevSelected) => {
            const newSet = new Set(prevSelected);
            if (enabled) {
              newSet.add(permissionKey);
            } else {
              newSet.delete(permissionKey);
            }
            return newSet;
          });
        });

        return newPermissions;
      });
    },
    [onPermissionsChange]
  );

  // Toggle all permissions for all entities
  const toggleAllModulePermissions = useCallback(
    (enabled: boolean) => {
      setPermissions(() => {
        const newPermissions: RolePermissionsByEntity = {};
        const newSelected = new Set<string>();

        PERMISSION_ENTITIES.forEach((entity) => {
          newPermissions[entity.key] = {};
          entity.actions.forEach((action) => {
            newPermissions[entity.key][action.key] = enabled;
            if (enabled) {
              newSelected.add(`${entity.key}:${action.key}`);
            }
          });
        });

        setSelectedPermissions(newSelected);

        return newPermissions;
      });
    },
    [onPermissionsChange]
  );

  // Reset permissions to original state
  const resetPermissions = useCallback(() => {
    setSelectedPermissions(new Set(originalPermissions));
    setPermissions(initialPermissions);

    if (onPermissionsChange) {
      onPermissionsChange(initialPermissions, false);
    }
  }, [initialPermissions, originalPermissions, onPermissionsChange]);

  // Validate permissions
  const validatePermissions = useCallback((): PermissionValidationResult => {
    const errors = [];

    if (selectedPermissions.size === 0) {
      errors.push({
        field: "permissions",
        message: "At least one permission must be selected",
      });
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }, [selectedPermissions]);

  // Get counts
  const getSelectedPermissionsCount = useCallback(
    () => selectedPermissions.size,
    [selectedPermissions]
  );

  const getTotalPermissionsCount = useCallback(() => {
    return PERMISSION_ENTITIES.reduce(
      (total, entity) => total + entity.actions.length,
      0
    );
  }, []);

  // Placeholder for save function (will be implemented in the modal)
  const savePermissions = useCallback(async () => {
    // This will be implemented in the component using this hook
    throw new Error("savePermissions must be implemented by the component");
  }, []);

  return {
    permissions,
    selectedPermissions,
    originalPermissions,
    hasChanges: hasChanges(),
    validationResult: validatePermissions(),
    togglePermission,
    toggleAllEntityPermissions,
    toggleAllModulePermissions,
    resetPermissions,
    savePermissions,
    getSelectedPermissionsCount,
    getTotalPermissionsCount,
  };
};

export default usePermissionState;
